import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DragEvent } from 'react';
import { TicketCard } from '@/components/TicketCard';
import { Skeleton } from '@/components/states/Skeleton';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { AssigneeFilter } from '@/components/AssigneeFilter';
import { TicketBlockedChip } from '@/components/TicketBlockedChip';
import { TicketAgeChip } from '@/components/TicketAgeChip';
import { useProgress } from '@/contexts/ProgressContext';
import { api, ApiClientError } from '@/api';
import { filterTicketsByAssignee } from '@/lib/ticket-meta';
import type { TicketMeta, TicketStatus } from '@/types';

/**
 * Board (Kanban) view · per Stage C §2.4 + Q3=A 2-tab Sprint screen pattern.
 *
 * **RISO Phase C refresh (16 MAY 2026 · slot_riso_sprint_board_ticket pick c):**
 * 3-column model preserved (To Do · In Progress · Done) · prototype's blocked
 * column collapses к card-level chip (status='locked' OR progress.blockedReason).
 * Card composition richer per prototype: priority pill · kind label · age-in-
 * column staleness · blocked chip · «cur» badge for lastTouched ticket. Adds
 * board-toolbar above grid с filter chips (тип · приоритет) + meta line
 * (X из Y видно · ~N мин · M% завершено).
 *
 * Phase 3.4a baseline: Board is rendered INLINE inside Sprint.tsx for Block 2+
 * sprints (NOT a separate screen). Sprint shell wraps Board в NavShell.
 *
 * Columns:
 *  - status='review' renders в «In Progress» с amber «🟡 в ревью» indicator
 *  - status='locked' renders в «To Do» с «🔒 заблокирован» chip
 *  - status='backlog' filtered out (Backlog tab handles it)
 *
 * Drag-drop per B2=C (HTML5 drag · lifted card · column tint on hover).
 *
 * Phase 3.3 storage: writes via existing `useProgress` setter for immediate
 * UI feedback + parallel `api.progress.patchTicket` fire-and-forget.
 */

type ColumnKey = 'todo' | 'in-progress' | 'done';

const COLUMNS: ReadonlyArray<{ key: ColumnKey; title: string }> = [
  { key: 'todo', title: 'К работе · To Do' },
  { key: 'in-progress', title: 'В работе · In Progress' },
  { key: 'done', title: 'Сделано · Done' },
];

function visibleStatusForColumn(status: TicketStatus): ColumnKey | null {
  if (status === 'todo') return 'todo';
  if (status === 'in-progress' || status === 'review') return 'in-progress';
  if (status === 'done') return 'done';
  // backlog · locked filtered out (handled in Backlog tab · W2 contract preserved).
  // RISO Phase C `🔒 заблокирован` chip triggers off future progress.blockedReason
  // field (CF-C-blocked-reason-field к Chat 2b) · not off status='locked'.
  return null;
}

/** RISO Phase C · type-filter chip values · matches prototype tickets kind. */
type TypeFilter = 'all' | 'lesson' | 'master' | 'practice' | 'bug' | 'test';
type PriorityFilter = 'all' | 'P0' | 'P1' | 'P2' | 'P3';

const TYPE_FILTERS: ReadonlyArray<{ v: TypeFilter; l: string }> = [
  { v: 'all', l: 'все' },
  { v: 'lesson', l: 'уроки' },
  { v: 'master', l: 'master' },
  { v: 'practice', l: 'практика' },
  { v: 'bug', l: 'bug' },
  { v: 'test', l: 'final' },
];

const PRI_FILTERS: ReadonlyArray<PriorityFilter> = ['all', 'P0', 'P1', 'P2', 'P3'];

/** Maps TicketMeta.type (canonical) к filter chip values. */
function ticketKindForFilter(type: string): TypeFilter | 'other' {
  if (type === 'docs') return 'lesson';
  if (type === 'master' || type === 'mission') return 'master';
  if (type === 'practice') return 'practice';
  if (type === 'bug') return 'bug';
  if (type === 'test') return 'test';
  return 'other';
}

// `ageInDays` helper removed 18 MAY · superseded by `ticketAgeInColumnDays`
// helper (src/lib/ticket-meta.ts @ cfa3c1a) consumed via TicketAgeChip component.

interface BoardProps {
  sprintId: string;
  onTicket?: (id: string) => void;
}

export function Board({ sprintId, onTicket }: BoardProps) {
  const { progress, setTicketStatus } = useProgress();
  const [tickets, setTickets] = useState<TicketMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoverColumn, setHoverColumn] = useState<ColumnKey | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [blockedHint, setBlockedHint] = useState<string | null>(null);

  // RISO Phase C · filter state · type + priority chips. Q-5 assignee filter
  // added-back 18 MAY (CHAT_2B Q-5 Genie adopt · AssigneeFilter component @ fecf34b).
  // `undefined` = no filter («Все») · `null` = unassigned filter («Никто») · string = userId.
  const [fType, setFType] = useState<TypeFilter>('all');
  const [fPri, setFPri] = useState<PriorityFilter>('all');
  const [fAssignee, setFAssignee] = useState<string | null | undefined>(undefined);
  // slot_t00_ftue_rewrite (7 JUN 2026 · Genie mobile smoke): на ≤480px фильтры
  // сворачиваются за кнопку «фильтры» — шапка доски съедала пол-экрана.
  // Desktop не затронут (кнопка скрыта CSS, .bt-filters виден всегда).
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersActive = fType !== 'all' || fPri !== 'all';

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ticketsRes = await api.staticContent.getSprintTickets(sprintId);
      setTickets(ticketsRes);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Не удалось загрузить тикеты');
    } finally {
      setLoading(false);
    }
  }, [sprintId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  /** Apply type + priority + assignee filters before grouping into columns. */
  const filteredTickets = useMemo(() => {
    // Q-5 assignee filter · compute matching ticket id set when filter active
    const allowedByAssignee =
      fAssignee === undefined
        ? null // no filter
        : new Set(filterTicketsByAssignee(progress.tickets, fAssignee));
    return tickets.filter((t) => {
      if (fType !== 'all' && ticketKindForFilter(t.type) !== fType) return false;
      if (fPri !== 'all' && t.priority !== fPri) return false;
      if (allowedByAssignee !== null && !allowedByAssignee.has(t.id)) return false;
      return true;
    });
  }, [tickets, fType, fPri, fAssignee, progress.tickets]);

  const grouped = useMemo(() => {
    const buckets: Record<ColumnKey, TicketMeta[]> = {
      todo: [],
      'in-progress': [],
      done: [],
    };
    for (const ticket of filteredTickets) {
      const status = progress.tickets[ticket.id]?.status ?? 'todo';
      const col = visibleStatusForColumn(status);
      if (col) buckets[col].push(ticket);
    }
    return buckets;
  }, [filteredTickets, progress.tickets]);

  // Meta line: counts · sum estimate · pct done (across filtered).
  const totalEstFiltered = filteredTickets.reduce((s, t) => s + (t.estimateMin ?? 0), 0);
  const doneCntAll = tickets.filter((t) => progress.tickets[t.id]?.status === 'done').length;
  const progressPct = tickets.length === 0 ? 0 : Math.round((doneCntAll / tickets.length) * 100);

  const moveTicket = useCallback(
    (ticketId: string, target: ColumnKey) => {
      const newStatus: TicketStatus = target;
      setTicketStatus(ticketId, newStatus);
      api.progress.patchTicket(ticketId, { status: newStatus }).catch((err: unknown) => {
        if (err instanceof ApiClientError) {
          console.warn('[Board] failed to sync ticket status', { ticketId, code: err.code });
        }
      });
    },
    [setTicketStatus],
  );

  /**
   * Done-column gate · per Genie 8 MAY: «закрыть тикет нельзя пока не закрыты
   * критерии и квик чек в самом тикете». Mirrors the Ticket.tsx close-button
   * disabled rule (`acAllDone && (!quickCheck || quickCheckPassed)`).
   */
  const canMoveToDone = useCallback(
    (ticketId: string): { ok: boolean; reason?: string } => {
      const ticket = tickets.find((t) => t.id === ticketId);
      if (!ticket) return { ok: false, reason: 'Тикет не найден' };
      const tp = progress.tickets[ticketId];
      const acTotal = ticket.ac.length;
      const acDone = acTotal === 0 ? 0 : ticket.ac.filter((a) => tp?.acChecked?.[a.id]).length;
      const acRemaining = acTotal - acDone;
      const hasQc = !!ticket.quickCheck;
      const qcPassed = !hasQc || tp?.quickCheck?.state === 'correct';
      if (acRemaining > 0) {
        return {
          ok: false,
          reason: `Сначала отметь галочки в тикете — осталось ${acRemaining}${hasQc && !qcPassed ? ' + «Проверка себя»' : ''}`,
        };
      }
      if (!qcPassed) {
        return { ok: false, reason: 'Сначала ответь на «Проверку себя» в тикете' };
      }
      return { ok: true };
    },
    [tickets, progress.tickets],
  );

  const handleDragStart = (ticketId: string) => (e: DragEvent<HTMLDivElement>) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ticketId);
    setDraggingId(ticketId);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setHoverColumn(null);
  };

  const handleDragOver = (col: ColumnKey) => (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (hoverColumn !== col) setHoverColumn(col);
  };

  const handleDragLeave = (col: ColumnKey) => () => {
    if (hoverColumn === col) setHoverColumn(null);
  };

  const handleDrop = (col: ColumnKey) => (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const ticketId = e.dataTransfer.getData('text/plain');
    if (!ticketId) return;
    setHoverColumn(null);
    setDraggingId(null);
    const currentStatus = progress.tickets[ticketId]?.status ?? 'todo';
    if (visibleStatusForColumn(currentStatus) === col) return;

    if (col === 'done') {
      const guard = canMoveToDone(ticketId);
      if (!guard.ok) {
        setBlockedHint(guard.reason ?? 'Тикет ещё не закрывается');
        window.setTimeout(() => setBlockedHint(null), 3500);
        return;
      }
    }

    moveTicket(ticketId, col);
  };

  const draggingBlockedFromDone = useMemo(() => {
    if (!draggingId) return false;
    return !canMoveToDone(draggingId).ok;
  }, [draggingId, canMoveToDone]);

  const lastTouched = progress.last?.ticketId;

  return (
    <div className="board-inline">
      {blockedHint && (
        <div className="board-blocked-hint" role="status" aria-live="polite" data-testid="board-blocked-hint">
          ⚠ {blockedHint}
        </div>
      )}

      {/* RISO Phase C · board toolbar · meta line + filter chips
          (prototype-fidelity per screens-dashboard.jsx lines 341-388). */}
      {!loading && !error && (
        <div className="board-toolbar" data-testid="board-toolbar">
          <div className="bt-meta">
            <span>
              {filteredTickets.length} из {tickets.length} видно
            </span>
            <span className="dot" aria-hidden="true">·</span>
            <span>{totalEstFiltered} мин в очереди</span>
            <span className="dot" aria-hidden="true">·</span>
            <span>
              <strong className="bt-pct">{progressPct}%</strong> завершено
            </span>
          </div>
          <button
            type="button"
            className="bt-filters-toggle"
            data-testid="bt-filters-toggle"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
          >
            фильтры {filtersActive ? '·' : ''} {filtersOpen ? '▴' : '▾'}
          </button>
          <div className={`bt-filters${filtersOpen ? ' bt-filters--open' : ''}`}>
            <div className="bt-fset" role="group" aria-label="Фильтр по типу">
              <span className="bt-flab">тип</span>
              {TYPE_FILTERS.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  className={`bt-fchip ${fType === o.v ? 'on' : ''}`}
                  onClick={() => setFType(o.v)}
                  aria-pressed={fType === o.v}
                >
                  {o.l}
                </button>
              ))}
            </div>
            <div className="bt-fset" role="group" aria-label="Фильтр по приоритету">
              <span className="bt-flab">приоритет</span>
              {PRI_FILTERS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`bt-fchip ${fPri === p ? 'on' : ''} ${p !== 'all' ? `p-${p}` : ''}`}
                  onClick={() => setFPri(p)}
                  aria-pressed={fPri === p}
                >
                  {p === 'all' ? 'все' : p}
                </button>
              ))}
            </div>
            {/* Q-5 multi-assignee filter (CHAT_2B Genie adopt 16 MAY · component @ fecf34b).
                Add-back UI dropped в RISO C 16 MAY · now active с assignees: string[] field.
                Rendered outside `.bt-fset` wrapper · AssigneeFilter has own styling. */}
            <AssigneeFilter
              tickets={progress.tickets}
              selected={fAssignee}
              onChange={setFAssignee}
            />
          </div>
        </div>
      )}

      {loading && (
        <div className="board-grid" data-testid="board-loading">
          {COLUMNS.map((c) => (
            <section key={c.key} className="board-column">
              <header className="board-column__head">
                <span className="board-column__title">{c.title}</span>
              </header>
              <Skeleton.List count={2} />
            </section>
          ))}
        </div>
      )}

      {!loading && error && (
        <ErrorState body={error} retry={{ onClick: () => void fetchData() }} />
      )}

      {!loading && !error && (
        <div className="board-grid" data-testid="board-grid" role="region" aria-label="Канбан-доска спринта">
          {COLUMNS.map((col) => {
            const items = grouped[col.key];
            const isHover = hoverColumn === col.key;
            const isDoneColBlocked = col.key === 'done' && draggingBlockedFromDone;
            // Время колонки (фидбек Genie 11 JUL): «К работе» — оценка «~N мин»
            // (реального времени ещё нет), «В работе»/«Сделано» — РЕАЛЬНОЕ время
            // из achievements.activityByTicket (трекер useScreenTime). Пока по
            // тикетам колонки ничего не накоплено — цифру не показываем (не врём
            // нулём: у старых сейвов per-ticket среза нет).
            const byTicket = progress.achievements?.activityByTicket ?? {};
            const colSum =
              col.key === 'todo'
                ? items.reduce((s, t) => s + (t.estimateMin ?? 0), 0)
                : Math.round(
                    items.reduce((s, t) => s + (byTicket[t.id] ?? 0), 0) / 60,
                  );
            const colSumLabel =
              col.key === 'todo' ? `~${colSum} мин` : `${colSum} мин`;
            const colSumAria =
              col.key === 'todo'
                ? `около ${colSum} минут по оценке`
                : `${colSum} минут практики по тикетам колонки`;
            return (
              <section
                key={col.key}
                className={`board-column${isHover ? ' board-column--hover' : ''}${isDoneColBlocked ? ' board-column--blocked' : ''}`}
                data-column={col.key}
                onDragOver={handleDragOver(col.key)}
                onDragLeave={handleDragLeave(col.key)}
                onDrop={handleDrop(col.key)}
                aria-label={`Колонка ${col.title}`}
              >
                <header className="board-column__head">
                  <span className="board-column__title">{col.title}</span>
                  <span className="board-column__count" aria-label={`${items.length} тикетов`}>
                    {items.length}
                  </span>
                  {items.length > 0 && colSum > 0 && (
                    <span className="board-column__sum" aria-label={colSumAria}>
                      {colSumLabel}
                    </span>
                  )}
                </header>
                <div className="board-column__body">
                  {items.length === 0 ? (
                    <EmptyState
                      compact
                      title="Пока пусто"
                      body={col.key === 'todo' ? 'Перетащи тикет сюда' : ''}
                    />
                  ) : (
                    items.map((t) => {
                      const tp = progress.tickets[t.id];
                      const status = tp?.status ?? 'todo';
                      const isReviewIndicator = status === 'review';
                      const isCurrent = t.id === lastTouched;
                      return (
                        <div
                          key={t.id}
                          className={`board-card-wrap${draggingId === t.id ? ' board-card-wrap--dragging' : ''}${isCurrent ? ' board-card-wrap--current' : ''}`}
                          draggable
                          onDragStart={handleDragStart(t.id)}
                          onDragEnd={handleDragEnd}
                          data-testid={`board-card-${t.id}`}
                        >
                          {isReviewIndicator && (
                            <span className="board-card-review-tag" aria-label="В ревью">
                              🟡 в ревью
                            </span>
                          )}
                          {/* Q-3 (CHAT_2B Genie adopt 16 MAY · component @ 7e8cdc0):
                              «🔒 заблокирован» chip · replaces inline JSX · null-render
                              defensive если blockedReason absent. */}
                          <TicketBlockedChip ticket={tp} compact />
                          {/* Q-4 (CHAT_2B Genie adopt 16 MAY · component @ 7e8cdc0):
                              age-in-column chip с tier 0/1/2 visual escalation ·
                              replaces inline JSX + ageInDays helper · null-render
                              defensive если no timestamp. */}
                          {/* Per Genie 28 JUN: чип «N дн в колонке» показываем только
                              в «В работе». В «Сделано» всё уже сделано, в «К работе» ещё
                              не начато — возраст там не нужен. */}
                          {col.key === 'in-progress' && <TicketAgeChip ticket={tp} />}
                          {isCurrent && (
                            <span className="board-card-now" aria-label="Сейчас здесь" data-testid={`board-now-${t.id}`}>
                              ·●· сейчас здесь
                            </span>
                          )}
                          <TicketCard
                            ticket={t}
                            status={status}
                            onClick={onTicket ? () => onTicket(t.id) : undefined}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
